import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, LogIn, Search, ShoppingBag, Sparkles, Tags, TrendingUp } from "lucide-react";
import { BannerCarousel } from "@/components/BannerCarousel";
import { ProductCard } from "@/components/ProductCard";
import { SeoHead } from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchCategories, fetchProducts } from "@/lib/api";
import { SITE_DESCRIPTION, SITE_NAME, absoluteUrl, buildCategoryUrl, buildProductUrl } from "@/lib/site";

const ITEMS_PER_PAGE = 20;

const Index = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const selectedCategory = searchParams.get("category") || "all";
  const selectedSubcategory = searchParams.get("subcategory") || "all";
  const currentPage = Number.parseInt(searchParams.get("page") || "1", 10);

  const setSelectedCategory = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value === "all") {
      params.delete("category");
      params.delete("subcategory");
    } else {
      params.set("category", value);
      params.delete("subcategory");
    }
    params.delete("page");
    setSearchParams(params);
  };

  const setSelectedSubcategory = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value === "all") {
      params.delete("subcategory");
    } else {
      params.set("subcategory", value);
    }
    params.delete("page");
    setSearchParams(params);
  };

  const setPage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page <= 1) {
      params.delete("page");
    } else {
      params.set("page", String(page));
    }
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: () => fetchProducts(),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const subcategories = useMemo(() => {
    if (selectedCategory === "all") {
      return [];
    }

    return [
      ...new Set(
        products
          .filter((product) => product.category === selectedCategory && product.subcategory)
          .map((product) => product.subcategory as string),
      ),
    ].sort();
  }, [products, selectedCategory]);

  const filtered = useMemo(() => products.filter((product) => {
    const name = product.item_name.toLowerCase();
    const matchSearch = name.includes(search.toLowerCase());
    const matchCategory = selectedCategory === "all" || product.category === selectedCategory;
    const matchSubcategory = selectedSubcategory === "all" || product.subcategory === selectedSubcategory;
    return matchSearch && matchCategory && matchSubcategory;
  }), [products, search, selectedCategory, selectedSubcategory]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const bannerProducts = products.filter((product) => product.banner);
  const featuredProducts = products.filter((product) => product.featured).slice(0, 10);
  const topSellers = [...products]
    .filter((product) => product.sales)
    .sort((a, b) => {
      const salesA = Number.parseInt(String(a.sales).replace(/\D/g, ""), 10) || 0;
      const salesB = Number.parseInt(String(b.sales).replace(/\D/g, ""), 10) || 0;
      return salesB - salesA;
    })
    .slice(0, 10);

  const showCategorySections = selectedCategory === "all" && !search;
  const categoryGroups = useMemo(() => {
    if (!showCategorySections) {
      return [];
    }

    const grouped = categories.map((category) => ({
      name: category,
      items: products.filter((product) => product.category === category),
    }));
    const withoutCategory = products.filter((product) => !product.category);

    if (withoutCategory.length > 0) {
      grouped.push({ name: "Outros", items: withoutCategory });
    }

    return grouped.sort((a, b) => b.items.length - a.items.length);
  }, [categories, products, showCategorySections]);

  const selectedCategoryCount = filtered.length;
  const seoTitle = selectedCategory === "all"
    ? "Achadinhos, ofertas e produtos baratos"
    : `${selectedCategory}${selectedSubcategory !== "all" ? ` - ${selectedSubcategory}` : ""} com ofertas e achadinhos`;
  const seoDescription = selectedCategory === "all"
    ? "Descubra achadinhos, produtos baratos, utilidades, eletronicos e ofertas escolhidas para comprar melhor na NetLand Shop."
    : `Veja ofertas de ${selectedCategory.toLowerCase()}${selectedSubcategory !== "all" ? ` em ${selectedSubcategory.toLowerCase()}` : ""} com links atualizados, produtos em destaque e atalhos para comprar melhor.`;
  const canonicalPath = (() => {
    const params = new URLSearchParams();
    if (selectedCategory !== "all") {
      params.set("category", selectedCategory);
    }
    if (selectedSubcategory !== "all") {
      params.set("subcategory", selectedSubcategory);
    }
    if (safePage > 1) {
      params.set("page", String(safePage));
    }
    const query = params.toString();
    return query ? `/?${query}` : "/";
  })();

  const homeSchema = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: absoluteUrl("/"),
      logo: absoluteUrl("/favicon.ico"),
      description: SITE_DESCRIPTION,
      sameAs: [
        "https://www.instagram.com/netlandtecnologia",
        "https://www.facebook.com/netlandti",
        "https://www.linkedin.com/company/netland",
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: absoluteUrl("/"),
      description: SITE_DESCRIPTION,
      inLanguage: "pt-BR",
      potentialAction: {
        "@type": "SearchAction",
        target: `${absoluteUrl("/")}?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: selectedCategory === "all" ? `${SITE_NAME} - Home` : `${selectedCategory} | ${SITE_NAME}`,
      url: absoluteUrl(canonicalPath),
      description: seoDescription,
      isPartOf: {
        "@type": "WebSite",
        name: SITE_NAME,
        url: absoluteUrl("/"),
      },
    },
  ];

  const renderSectionCard = (title: string, items: typeof products, action?: ReactNode, description?: string) => (
    <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div className="flex flex-col gap-2 border-b border-border px-4 py-4 md:flex-row md:items-center md:justify-between md:px-5">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {action}
      </div>
      <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 md:grid-cols-4 md:p-5 lg:grid-cols-5">
        {items.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onClick={() => navigate(buildProductUrl(product.id, product.item_name))}
          />
        ))}
      </div>
    </section>
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        canonicalPath={canonicalPath}
        jsonLd={homeSchema}
      />

      <div className="bg-primary text-primary-foreground">
        <div className="container mx-auto flex items-center justify-between px-4 py-1.5 text-xs">
          <span>Achadinhos atualizados para encontrar ofertas melhores sem perder tempo.</span>
          <Link to="/admin" className="flex items-center gap-1 hover:underline">
            <LogIn className="h-3 w-3" />
            Admin
          </Link>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-primary shadow-md">
        <div className="container mx-auto flex items-center gap-4 px-4 py-3">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <ShoppingBag className="h-8 w-8 text-primary-foreground" />
            <span className="hidden text-xl font-bold text-primary-foreground sm:block">{SITE_NAME}</span>
          </Link>

          <form
            className="relative flex-1"
            onSubmit={(event) => {
              event.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar achadinhos, utilidades e ofertas"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-10 rounded-sm border-none bg-background pl-9"
              aria-label="Buscar produtos"
            />
          </form>
        </div>

        {categories.length > 0 && (
          <div className="border-t border-primary-foreground/10 bg-primary/90">
            <div className="container mx-auto flex flex-wrap items-center gap-2 px-4 py-2">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="h-8 w-[180px] border-primary-foreground/20 bg-primary-foreground/10 text-sm text-primary-foreground">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {subcategories.length > 0 && (
                <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                  <SelectTrigger className="h-8 w-[190px] border-primary-foreground/20 bg-primary-foreground/10 text-sm text-primary-foreground">
                    <SelectValue placeholder="Subcategoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as subcategorias</SelectItem>
                    {subcategories.map((subcategory) => (
                      <SelectItem key={subcategory} value={subcategory}>{subcategory}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="container mx-auto flex-1 px-4 py-6 pb-12">
        <section className="rounded-3xl border border-border bg-gradient-to-br from-card via-card to-muted/70 px-5 py-8 shadow-sm md:px-8">
          <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr] lg:items-center">
            <div className="space-y-4">
              <p className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                Achadinhos com curadoria
              </p>
              <h1 className="max-w-3xl text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                {selectedCategory === "all"
                  ? "Ofertas, produtos baratos e achadinhos para comprar melhor"
                  : `Ofertas de ${selectedCategory}${selectedSubcategory !== "all" ? ` em ${selectedSubcategory}` : ""}`}
              </h1>
              <p className="max-w-3xl text-base leading-7 text-muted-foreground md:text-lg">
                {selectedCategory === "all"
                  ? "A NetLand Shop organiza uma vitrine de utilidades, eletronicos, casa, cozinha e itens em destaque para quem quer encontrar boas oportunidades com mais facilidade."
                  : `Selecionamos produtos de ${selectedCategory.toLowerCase()} com navegacao simples, comparacao visual rapida e links diretos para a oferta. Hoje ha ${selectedCategoryCount} itens nesta selecao.`}
              </p>
              <div className="flex flex-wrap gap-3">
                <Button size="lg" onClick={() => window.scrollTo({ top: 720, behavior: "smooth" })}>
                  Ver ofertas agora
                </Button>
                {selectedCategory !== "all" ? (
                  <Button variant="outline" size="lg" onClick={() => setSelectedCategory("all")}>
                    Voltar para todas as categorias
                  </Button>
                ) : null}
              </div>
            </div>

            <aside className="grid gap-3 rounded-2xl border border-border bg-background/70 p-4">
              <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
                <Tags className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Categorias organizadas</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Encontre produtos por categoria e subcategoria com navegacao simples e visual mais facil de comparar.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
                <TrendingUp className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Ofertas em destaque</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Veja produtos em destaque, paginas mais completas e atalhos diretos para conferir cada oferta.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </section>

        {categories.length > 0 && selectedCategory === "all" && !search && (
          <section className="mt-8">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Categorias em destaque</h2>
                <p className="text-sm text-muted-foreground">
                  Explore atalhos internos para navegar pelos produtos com mais rapidez.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.slice(0, 12).map((category) => (
                <Link
                  key={category}
                  to={buildCategoryUrl(category)}
                  className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  {category}
                </Link>
              ))}
            </div>
          </section>
        )}

        {selectedCategory !== "all" && (
          <nav aria-label="Breadcrumb" className="mt-8 text-sm text-muted-foreground">
            <ol className="flex flex-wrap items-center gap-2">
              <li><Link to="/" className="hover:text-primary">Inicio</Link></li>
              <li>/</li>
              <li>
                <Link to={buildCategoryUrl(selectedCategory)} className="hover:text-primary">
                  {selectedCategory}
                </Link>
              </li>
              {selectedSubcategory !== "all" ? (
                <>
                  <li>/</li>
                  <li className="text-foreground">{selectedSubcategory}</li>
                </>
              ) : null}
            </ol>
          </nav>
        )}

        <section className="mt-8">
          {isLoading ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {Array.from({ length: 10 }).map((_, index) => (
                <div key={index} className="h-72 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-20 text-muted-foreground">
              <ShoppingBag className="mb-4 h-16 w-16 opacity-30" />
              <p className="text-lg">Nenhum produto encontrado para este filtro.</p>
            </div>
          ) : showCategorySections ? (
            <div className="space-y-8">
              {bannerProducts.length > 0 ? <BannerCarousel products={bannerProducts} /> : null}

              {featuredProducts.length > 0 ? renderSectionCard(
                "Produtos em destaque",
                featuredProducts,
                undefined,
                "Selecao com produtos em destaque e boa apresentacao visual.",
              ) : null}

              {topSellers.length > 0 ? renderSectionCard(
                "Mais vendidos",
                topSellers,
                undefined,
                "Itens com historico de vendas e boa aprovacao dos clientes.",
              ) : null}

              {categoryGroups.map(({ name, items }) => (
                items.length > 0 ? renderSectionCard(
                  `${name} (${items.length})`,
                  items,
                  name !== "Outros" ? (
                    <Button variant="link" size="sm" onClick={() => setSelectedCategory(name)} className="text-primary">
                      Ver todos
                    </Button>
                  ) : undefined,
                  `Produtos de ${name.toLowerCase()} organizados para facilitar a navegacao e a descoberta de ofertas.`,
                ) : null
              ))}
            </div>
          ) : (
            <>
              <div className="mb-4 flex items-end justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-semibold text-foreground">
                    {selectedCategory === "all" ? "Todos os produtos" : selectedCategory}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {filtered.length} produto{filtered.length !== 1 ? "s" : ""} encontrado{filtered.length !== 1 ? "s" : ""}.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {paginated.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onClick={() => navigate(buildProductUrl(product.id, product.item_name))}
                  />
                ))}
              </div>

              {totalPages > 1 ? (
                <nav aria-label="Paginacao de produtos" className="mt-8 flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage <= 1}
                    onClick={() => setPage(safePage - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, index) => {
                      let pageNumber: number;
                      if (totalPages <= 7) {
                        pageNumber = index + 1;
                      } else if (safePage <= 4) {
                        pageNumber = index + 1;
                      } else if (safePage >= totalPages - 3) {
                        pageNumber = totalPages - 6 + index;
                      } else {
                        pageNumber = safePage - 3 + index;
                      }

                      return (
                        <Button
                          key={pageNumber}
                          variant={pageNumber === safePage ? "default" : "outline"}
                          size="sm"
                          className="h-9 w-9 p-0"
                          onClick={() => setPage(pageNumber)}
                          aria-label={`Ir para a pagina ${pageNumber}`}
                          aria-current={pageNumber === safePage ? "page" : undefined}
                        >
                          {pageNumber}
                        </Button>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(safePage + 1)}
                  >
                    Proximo
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </nav>
              ) : null}
            </>
          )}
        </section>

        <section className="mt-10 rounded-2xl border border-border bg-card p-5">
          <h2 className="text-xl font-semibold text-foreground">Como a NetLand Shop funciona</h2>
          <div className="mt-3 grid gap-4 md:grid-cols-3">
            <div>
              <h3 className="font-medium text-foreground">Curadoria de ofertas</h3>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                Organizamos produtos e categorias para facilitar comparacao visual rapida e descoberta de achadinhos.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-foreground">Acesso rapido as ofertas</h3>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                Ao abrir uma oferta, voce segue direto para a pagina do produto na loja parceira.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-foreground">Navegacao simples no mobile</h3>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                Busca, filtros e cards foram organizados para funcionar melhor em telas pequenas e acelerar o caminho ate a oferta.
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t bg-card py-8">
        <div className="container mx-auto grid gap-6 px-4 md:grid-cols-[1.3fr_1fr_1fr]">
          <div>
            <h2 className="text-base font-semibold text-foreground">{SITE_NAME}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Portal de achadinhos com ofertas selecionadas e produtos com bom potencial de custo-beneficio.
            </p>
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">Atalhos</h2>
            <div className="mt-2 flex flex-col gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary">Home</Link>
              {categories.slice(0, 5).map((category) => (
                <Link key={category} to={buildCategoryUrl(category)} className="hover:text-primary">
                  {category}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">Sobre a vitrine</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              A NetLand Shop funciona como uma vitrine para ajudar voce a encontrar produtos e ofertas com mais facilidade.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
